/* eslint-disable no-empty */
/**
 * AJAX Helper Utilities
 * Simplified AJAX operations with jQuery
 */

import Cookies from 'js-cookie';
import getDomain from './domain.js';
import { convertCamelToSnake, convertSnakeToCamel } from './string-converter.js';
import { showToast } from './toastr.js';

const TOKEN_KEY = 'PHPSESSID';

const redirectToLogin = () => {
    const domain = getDomain();

    Cookies.remove(TOKEN_KEY);

    sessionStorage.setItem('loginExpired', '1');
    window.location.replace(`/${domain}/auth.php?page=login_step_one`);
    return;
};

/**
 * Make AJAX request with enhanced error handling
 */
function ajaxRequest(url, options = {}) {
    return new Promise((resolve, reject) => {
        // Don't convert FormData objects - they need to be sent as-is
        const data = options.isFormData ? options.data : convertCamelToSnake(options.data) || {};

        // Special options for FormData
        const ajaxOptions = {
            url,
            method: options.method || 'GET',
            data,
            dataType: options.dataType || 'json',
            timeout: 300000,
            headers: options.headers || {}
        };

        // Add FormData-specific options
        if (options.isFormData) {
            ajaxOptions.processData = false; // Don't process data
            ajaxOptions.contentType = false; // Don't set content type
        }

        $.ajax(ajaxOptions)
            .done((response, _textStatus, _xhr) => {
                response = convertSnakeToCamel(response);
                if (typeof response === 'string') {
                    try {
                        response = JSON.parse(response);
                    } catch (e) {
                        resolve(response);
                        return;
                    }
                }
                resolve(response);
            })
            .fail((xhr, textStatus, errorThrown) => {
                let errorMessage = xhr.responseJSON ? xhr.responseJSON?.message : xhr.responseText?.message;

                switch (textStatus) {
                    case 'timeout':
                        errorMessage = 'リクエストがタイムアウトしました。';
                        break;
                    case 'error':
                        if (xhr.status === 0) {
                            errorMessage = 'ネットワークエラーが発生しました';
                        } else if (xhr.status === 401) {
                            redirectToLogin();
                        } else if ([400, 404, 422].includes(xhr.status)) {
                        } else if (xhr.status === 500) {
                            errorMessage = 'サーバーエラーが発生しました。';
                        } else if (xhr.status === 403) {
                            errorMessage = 'アクセスが拒否されました';
                        } else if (xhr.status === 409) {
                            errorMessage = xhr?.responseJSON?.message || 'リソースが衝突しました';
                        } else {
                            errorMessage = `HTTP ${xhr.status}: ${errorThrown}`;
                        }
                        break;
                    case 'parsererror':
                        errorMessage = 'レスポンスを解析できません。';
                        break;
                    default:
                        break;
                }

                if (options.showError) {
                    const skipToastForStatuses = options.skipToastForStatuses || [];
                    if (!skipToastForStatuses.includes(xhr.status)) {
                        if (Array.isArray(errorMessage)) {
                            errorMessage = errorMessage.join('\n');
                        }

                        showToast(errorMessage, 'error');
                    }
                }

                const err = new Error(errorMessage);
                err.statusCode = xhr.status;
                reject(err);
            });
    });
}

/**
 * GET request
 */
function get(url, params = {}, options = {}) {
    // Add cache-busting parameter to prevent browser caching
    const cacheBustingParams = {
        ...params,
        _t: Date.now() // Add timestamp to force fresh request
    };

    return ajaxRequest(url, {
        method: 'GET',
        data: cacheBustingParams,
        ...options
    });
}

/**
 * POST request
 */
function post(url, data = {}, options = {}) {
    const cacheBustingPayloads = {
        ...data,
        _t: Date.now() // Add timestamp to force fresh request
    };

    // For FormData, don't add cache-busting parameter as it can interfere with file uploads
    if (options.isFormData) {
        return ajaxRequest(url, {
            method: 'POST',
            data,
            isFormData: true,
            ...options
        });
    }

    // Add cache-busting parameter to prevent browser caching for regular objects
    return ajaxRequest(url, {
        method: 'POST',
        data: cacheBustingPayloads,
        ...options
    });
}

/**
 * PUT request
 */
function put(url, data = {}, options = {}) {
    // Add cache-busting parameter to prevent browser caching
    const cacheBustingPayloads = {
        ...data,
        _t: Date.now() // Add timestamp to force fresh request
    };

    return ajaxRequest(url, {
        method: 'PUT',
        data: cacheBustingPayloads,
        ...options
    });
}

/**
 * DELETE request
 */
function del(url, data = {}, options = {}) {
    // Add cache-busting parameter to prevent browser caching
    const cacheBustingPayloads = {
        ...data,
        _t: Date.now() // Add timestamp to force fresh request
    };

    return ajaxRequest(url, {
        method: 'DELETE',
        data: cacheBustingPayloads,
        ...options
    });
}

// Export functions
export { ajaxRequest, del, get, post, put };
